import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { initialsFromName } from "@/lib/initials";
import { HomeShell } from "@/shell/HomeShell";
import { Spinner } from "@/components/primitives";
import type { HomeData, StartResult, ProfileData } from "@/lib/types";
import { companyInitials, formatDate } from "@/lib/format";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function scoreTier(score: number): { stroke: string; label: string } {
  if (score <= 20) return { stroke: "#DC2626", label: "You're not doing everything you can" };
  if (score <= 50) return { stroke: "#D97706", label: "Keep practising — you're getting there" };
  if (score <= 89) return { stroke: "#16A34A", label: "You're on track — keep going" };
  return { stroke: "var(--accent)", label: "You going to smash this" };
}

function PrepRing({ score, hasCompleted }: { score: number; hasCompleted: boolean }) {
  const r = 62;
  const circ = 2 * Math.PI * r;
  const ghostFill = circ * 0.3;
  const fill = circ * (score / 100);
  const tier = scoreTier(score);
  return (
    <div className="relative shrink-0" style={{ width: 150, height: 150 }}>
      <svg
        width={150}
        height={150}
        viewBox="0 0 150 150"
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx={75}
          cy={75}
          r={r}
          fill="none"
          stroke="rgba(122,107,224,0.16)"
          strokeWidth={12}
        />
        {hasCompleted ? (
          <circle
            cx={75}
            cy={75}
            r={r}
            fill="none"
            stroke={tier.stroke}
            strokeWidth={12}
            strokeDasharray={`${fill} ${circ}`}
            strokeLinecap="round"
          />
        ) : (
          <circle
            cx={75}
            cy={75}
            r={r}
            fill="none"
            stroke="rgba(122,107,224,0.20)"
            strokeWidth={12}
            strokeDasharray={`${ghostFill} ${circ}`}
            strokeLinecap="round"
            strokeDashoffset={0}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {hasCompleted ? (
          <>
            <span
              className="font-mono font-extrabold"
              style={{ fontSize: 40, letterSpacing: "-0.03em", lineHeight: 1, color: "#1B1A24" }}
            >
              {score}%
            </span>
            <span
              className="font-mono uppercase text-[10px] mt-1"
              style={{ color: "var(--accent)", letterSpacing: "0.08em" }}
            >
              Ready
            </span>
          </>
        ) : (
          <>
            <span
              className="font-mono font-extrabold"
              style={{ fontSize: 28, letterSpacing: "-0.04em", lineHeight: 1, color: "#8C86A0" }}
            >
              0%
            </span>
            <span
              className="font-mono text-center mt-1"
              style={{ fontSize: 8, color: "#8C86A0", letterSpacing: "0.04em", lineHeight: 1.3, maxWidth: 72 }}
            >
              30% after first session
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function OnboardingStrip({ visits }: { visits: number }) {
  if (visits > 5) return null;
  const steps = [
    { done: true, title: "Upload CV & JD", body: "Personalise every session" },
    { done: true, title: "Complete drills", body: "Build muscle memory fast" },
    { done: false, title: "Practise your interview", body: "Full AI-led exec session" },
  ];
  return (
    <div
      style={{ background: "#F3F1FA", borderRadius: 10, marginBottom: 20, border: "1px solid #EFEBF7" }}
    >
      <div className="relative">
      <div className="flex items-center px-4 md:px-6 py-3 gap-4 md:gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span
          className="font-mono font-bold uppercase text-[10px] shrink-0 mr-2"
          style={{ color: "var(--accent)", letterSpacing: "0.08em" }}
        >
          How your preparation works
        </span>
        {steps.map((step, i) => (
          <div
            key={i}
            className="flex items-center gap-2.5 shrink-0 px-4"
            style={{ borderLeft: "1px solid rgba(27,26,36,0.08)" }}
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
              style={{ background: step.done ? "#22A06B" : "var(--accent)", color: "#fff" }}
            >
              {step.done ? "✓" : i + 1}
            </div>
            <div>
              <div className="text-[12.5px] font-semibold whitespace-nowrap" style={{ color: "#1B1A24" }}>
                {step.title}
              </div>
              <div className="text-[11px] whitespace-nowrap" style={{ color: "#7A7688" }}>
                {step.body}
              </div>
            </div>
          </div>
        ))}
        <span
          className="text-[10px] shrink-0 ml-2 font-mono"
          style={{ color: "#A5A2B0" }}
        >
          {visits}/5
        </span>
      </div>
      {/* Right-edge fade on mobile to hint at horizontal scroll */}
      <div
        className="absolute right-0 top-0 bottom-0 pointer-events-none md:hidden"
        style={{ width: 40, background: "linear-gradient(to right, transparent, #F3F1FA)" }}
      />
      </div>
    </div>
  );
}

function Card({
  children,
  className = "",
  style = {},
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`rounded-[18px] ${className}`}
      style={{
        background: "#fff",
        border: "1px solid #EFEBF7",
        boxShadow: "0 2px 10px rgba(27,26,36,0.05)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CardHeader({
  title,
  action,
  href,
}: {
  title: string;
  action?: string;
  href?: string;
}) {
  return (
    <div className="flex items-center justify-between px-5 pt-4 pb-0 mb-3.5">
      <span
        className="uppercase font-bold text-[10.5px] tracking-[0.07em]"
        style={{ color: "#A5A2B0" }}
      >
        {title}
      </span>
      {action && href && (
        <Link to={href} className="text-[12px] font-semibold" style={{ color: "var(--accent)" }}>
          {action} →
        </Link>
      )}
    </div>
  );
}

function KbDot({ status }: { status: "done" | "warn" | "empty" }) {
  const c = status === "done" ? "#22A06B" : status === "warn" ? "#FFC94D" : "#D8D5E2";
  return (
    <div
      style={{ width: 7, height: 7, borderRadius: "50%", background: c, flexShrink: 0 }}
    />
  );
}

function TypePill({ type }: { type: "practise" | "drill" }) {
  const isPractise = type === "practise";
  return (
    <span
      className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0"
      style={{
        background: isPractise ? "#EFEBFF" : "#FCEFEA",
        color: isPractise ? "#5B4CC7" : "#C05A2E",
        letterSpacing: "0.05em",
      }}
    >
      {isPractise ? "Practise" : "Drill"}
    </span>
  );
}

const MODE_BODIES: Record<string, string> = {
  "tmay-drill":
    "Your 2-minute opening statement, timed and scored. We flag filler words and grade structure: context → career arc → value proposition. Most senior candidates run 30 seconds over. This drill locks the shape and rhythm before tomorrow.",
  "pcar-drill":
    "Problem–Cause–Action–Result. We surface a scenario from your CV, coach you through each layer with follow-up pressure questions, then score evidence depth and specificity. Designed to stress-test the stories the panel is most likely to probe.",
  "outro-pitch":
    "Your final 90 seconds: genuine interest signal, one sharp commercial question, and a confident sign-off. Routinely underprepared at VP and C-suite level. This drill removes the hesitation so the closing impression lands with intention.",
  "question-asker":
    "You ask the coach questions instead. Practise incisive, exec-level questions that signal commercial curiosity and strategic depth. The drill that separates the candidates who prepared from those who didn't.",
  "full":
    "A full interview simulation: opening narrative, 6–8 competency and commercial questions, then your outro pitch. Scored across structure, evidence depth, delivery, and strategic thinking. The closest thing to the real interview before you walk into the room.",
};

export function Home() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    candidateApi.getProfile(token).then((r) => {
      setProfileLoaded(true);
      if (r.ok) setProfile(r as ProfileData);
    });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    candidateApi.getHomeData(token).then((r) => {
      setLoading(false);
      if (r.ok) setData(r as HomeData);
      else if ((r as { status?: number }).status === 401)
        // The emailed code is the only re-auth since IC-270; /login is gone.
        navigate(`/invite/${token}/verify`, { replace: true });
      else setError((r as { error?: string }).error ?? "Could not load home data.");
    });
  }, [token, navigate]);

  if (loading) {
    return (
      <HomeShell>
        <div className="flex justify-center py-20">
          <Spinner size={32} />
        </div>
      </HomeShell>
    );
  }
  if (error || !data) {
    return (
      <HomeShell>
        <div className="py-20 text-center text-slate-500">{error ?? "Something went wrong."}</div>
      </HomeShell>
    );
  }

  const logoUrl = data.branding?.logoUrlLight || undefined;
  const logoFilter = data.branding?.logoFilter;

  async function startSession(mode?: string) {
    if (!token) return;
    // Route to profile first if no master CV on record.
    if (profile !== null && !profile.hasCv) {
      navigate(`/invite/${token}/profile`);
      return;
    }
    setStarting(true);
    setStartError(null);
    const form = new FormData();
    if (mode) form.set("mode", mode);
    const r = await candidateApi.start(token, form);
    if (!r.ok) {
      setStarting(false);
      if ((r as { field?: string }).field === "cv") {
        navigate(`/invite/${token}/profile`);
        return;
      }
      setStartError((r as { error?: string }).error ?? "Could not start session.");
      return;
    }
    const sid = (r as StartResult).sessionId;
    if (sid) {
      navigate(`/invite/${token}/prep/${sid}`);
    } else {
      setStarting(false);
      setStartError("Session could not be started. Please try again.");
    }
  }

  const ctaProps = {
    onClick: () => startSession(),
    disabled: starting || !profileLoaded,
    style: { background: "#1B1A24" } as React.CSSProperties,
  };
  const ctaLabel = starting ? "Starting…" : "Start a practice session";
  // Expired invite the candidate has used before: they can review what they did,
  // but api_invite_start answers 410, so never offer a button that cannot work.
  const reviewOnly = Boolean(data.reviewOnly);

  const candidateInitials = initialsFromName(data.displayName || data.firstName);

  return (
    <HomeShell
      agencyName={data.agencyName}
      candidateInitials={candidateInitials}
      logoUrl={logoUrl}
      logoFilter={logoFilter}
    >
      {/* ponytail: extra mobile-only bottom padding so the sticky CTA (now stacked
          above the tab bar, not overlapping it) doesn't obscure the last card */}
      <div className="pb-32 md:pb-0">
      <OnboardingStrip visits={data.homeVisits} />

      {/* Prep Score Hero — matches Design System v3 1A */}
      <div
        className="relative overflow-hidden rounded-[22px] px-5 md:px-8 py-6 mb-6 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        style={{ background: "linear-gradient(155deg, #EFEBFF 0%, #F5E6F7 52%, #FCEFEA 100%)", border: "1px solid #ECE8F7" }}
      >
        {/* Decorative circle */}
        <div
          className="absolute pointer-events-none hidden md:block"
          style={{ right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.35)" }}
        />

        {/* Ring — centred on mobile, left-aligned on desktop */}
        <div className="relative flex flex-col items-center md:items-start gap-1.5 shrink-0">
          <PrepRing score={data.prepScore} hasCompleted={data.practiseCount > 0} />
        </div>

        {/* Greeting + role/company + buttons */}
        <div className="relative flex-1 text-center md:text-left">
          <p
            className="font-mono text-[11px] font-bold uppercase mb-1"
            style={{ color: "var(--accent)", letterSpacing: "0.08em" }}
          >
            {DAY_NAMES[new Date().getDay()]} · Calm start
          </p>
          <h1
            className="text-[34px] mb-1"
            style={{ fontFamily: "'Newsreader', serif", fontWeight: 500, letterSpacing: "-0.015em", color: "#1B1A24" }}
          >
            {data.practiseCount > 0 ? "You're nearly there, " : "Ready when you are, "}
            <em style={{ fontStyle: "italic", color: "var(--accent)" }}>
              {data.firstName?.split(" ")[0] || "there"}.
            </em>
          </h1>
          <p className="text-[13px] mb-5" style={{ color: "#5A5866" }}>
            {data.role} · {data.company}
          </p>
          {/* Shown at every width: on mobile both start CTAs are gone, so without
              this the screen would just be missing its main action with no reason. */}
          {reviewOnly && (
            <p
              className="mb-5 rounded-lg px-4 py-3 text-[13px] leading-relaxed"
              style={{ background: "rgba(122,107,224,0.08)", color: "#4A4658" }}
            >
              This invite has expired, so you cannot start a new practice session.
              Your past sessions and scorecards are still here to review.
            </p>
          )}
          {/* Desktop-only — mobile relies on the sticky bottom CTA bar instead, no duplicate button */}
          <div className="hidden md:flex justify-center md:justify-start gap-2.5">
            {!reviewOnly && (
              <button
                {...ctaProps}
                className="text-white text-[14px] font-semibold px-5 py-3 rounded-full whitespace-nowrap disabled:opacity-60 inline-flex items-center gap-2"
              >
                <span
                  className="inline-block shrink-0"
                  style={{ width: 0, height: 0, borderLeft: "8px solid #fff", borderTop: "5px solid transparent", borderBottom: "5px solid transparent" }}
                />
                {ctaLabel}
              </button>
            )}
            {data.latestScorecardSessionId && (
              <Link
                to={`/invite/${token}/scorecard/${data.latestScorecardSessionId}`}
                className="text-[14px] font-semibold px-[22px] py-3 rounded-full whitespace-nowrap"
                style={{ background: "#fff", color: "#1B1A24", boxShadow: "0 2px 8px rgba(27,26,36,0.06)" }}
              >
                View scorecard
              </Link>
            )}
          </div>
        </div>

        {/* Desktop-only stats column — mobile relies on the sticky bottom CTA bar instead */}
        <div
          className="relative hidden md:flex flex-col gap-3 items-end shrink-0 text-right pl-8"
          style={{ borderLeft: "1px solid rgba(122,107,224,0.18)" }}
        >
          <div>
            <div className="text-[22px] font-extrabold" style={{ color: "#1B1A24", lineHeight: 1 }}>{data.practiseCount}</div>
            <div className="text-[11px]" style={{ color: "#7A7688" }}>Session{data.practiseCount !== 1 ? "s" : ""}</div>
          </div>
          <div>
            <div className="text-[22px] font-extrabold" style={{ color: "#1B1A24", lineHeight: 1 }}>{data.drillCount}</div>
            <div className="text-[11px]" style={{ color: "#7A7688" }}>Drill{data.drillCount !== 1 ? "s" : ""}</div>
          </div>
          {data.avgScore != null && (
            <div>
              <div className="text-[22px] font-extrabold" style={{ color: "#2FA872", lineHeight: 1 }}>{data.avgScore}</div>
              <div className="text-[11px]" style={{ color: "#7A7688" }}>Avg score</div>
            </div>
          )}
        </div>
      </div>

      {/* Coach nudge strip */}
      {data.practiseCount > 0 && (
        <div
          className="rounded-2xl px-5 py-3.5 mb-6 flex items-center gap-3"
          style={{ background: "linear-gradient(135deg, #EFEBFF, #FCEFEA)" }}
        >
          <div className="w-5 h-5 rounded-full shrink-0" style={{ background: "#FFC94D" }} />
          <p className="text-[15px] italic" style={{ fontFamily: "'Newsreader', serif", color: "#3A3550" }}>
            {scoreTier(data.prepScore).label}
          </p>
        </div>
      )}

      {startError && (
        <p className="mt-3 text-[13px]" style={{ color: "#E1573F" }}>
          {startError}
        </p>
      )}

      {/* Card Grid — single column on mobile, 3 columns on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* KB Status */}
        <Card>
          <CardHeader
            title="Knowledge Base"
            {...(reviewOnly ? {} : { action: "Edit", href: `/invite/${token}/setup` })}
          />
          <div className="px-5 pb-4 flex flex-col gap-2">
            {[
              {
                label: "CV uploaded",
                status: data.kb.hasCv ? ("done" as const) : ("empty" as const),
                meta: data.kb.cvUploadedAt || (data.kb.hasCv ? "Uploaded" : "Not uploaded"),
              },
              {
                label: "Job description",
                status: data.kb.hasJd ? ("done" as const) : ("empty" as const),
                meta: data.kb.hasJd
                  ? data.kb.jdSource === "recruiter"
                    ? "Recruiter-provided"
                    : "Added by you"
                  : "Not provided",
              },
              {
                label: "Prep notes",
                status: data.kb.hasNotes ? ("warn" as const) : ("empty" as const),
                meta: data.kb.hasNotes ? "In progress" : "Not added",
              },
            ].map(({ label, status, meta }) => (
              <div key={label} className="flex items-center gap-2.5">
                <KbDot status={status} />
                <span
                  className="flex-1 text-[13.5px]"
                  style={{ color: status === "empty" ? "#9A97A6" : "#3A3550" }}
                >
                  {label}
                </span>
                <span
                  className="text-[11px]"
                  style={{
                    color: status === "done" ? "#B0AEBB" : status === "warn" ? "#C99A2E" : "#C6C3D0",
                  }}
                >
                  {meta}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* CV */}
        <Card>
          <CardHeader title="Your CV" />
          <div className="px-5 pb-4">
            <div
              className="text-[15px] font-bold mb-0.5"
              style={{ color: "#17131F" }}
            >
              {data.displayName || data.firstName} — CV
            </div>
            <div className="text-[12.5px] leading-snug mb-1" style={{ color: "#7A7688" }}>
              {data.role}
            </div>
            {data.kb.cvUploadedAt && (
              <div
                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold px-2.5 py-1 rounded-full mt-2"
                style={{ background: "#E2F5EC", color: "#1E8557" }}
              >
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#2FA872" }} />
                Updated {data.kb.cvUploadedAt}
              </div>
            )}
            {!reviewOnly && (
            <button
              onClick={() => navigate(`/invite/${token}/profile`)}
              className="w-full mt-4 py-2.5 rounded-full text-[13px] font-semibold transition-colors"
              style={{ background: "#F7F5FC", border: "1px solid #ECE8F7", color: "#5B4CC7" }}
            >
              ↑ Update CV
            </button>
            )}
          </div>
        </Card>

        {/* Prep Notes */}
        <Card>
          <CardHeader title="Prep Notes" action="Edit" href={`/invite/${token}/setup`} />
          <div className="px-5 pb-4">
            {data.kb.hasNotes ? (
              <>
                <p
                  className="text-[13.5px] leading-relaxed line-clamp-4"
                  style={{ color: "#4A4260", borderLeft: "3px solid #7A6BE0", paddingLeft: 12 }}
                >
                  {data.kb.notesPreview}
                </p>
                <Link
                  to={`/invite/${token}/setup`}
                  className="inline-block mt-3 text-[12px] font-semibold"
                  style={{ color: "var(--accent)" }}
                >
                  View all notes →
                </Link>
              </>
            ) : (
              <p className="text-[13px] italic" style={{ color: "#8C86A0" }}>
                No prep notes added yet.
              </p>
            )}
          </div>
        </Card>

        {/* Past Roles — spans 2 columns on desktop, full width on mobile */}
        <Card className="md:col-span-2">
          <CardHeader title="Your Briefs" action="View all" href={`/invite/${token}/briefs`} />
          <div className="px-5 pb-2">
            {data.pastRoles.length === 0 ? (
              <p className="text-[13px] py-2 italic" style={{ color: "#8C86A0" }}>
                Your roles will appear here as your consultant sends invites.
              </p>
            ) : (
              data.pastRoles.slice(0, 4).map((role, i) => (
                <div
                  key={role.token}
                  className="flex items-center gap-3 py-2.5"
                  style={{
                    borderBottom:
                      i < Math.min(data.pastRoles.length, 4) - 1 ? "1px solid #F4F2F9" : "none",
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0"
                    style={{ background: "#EFEBFF", color: "var(--accent)" }}
                  >
                    {companyInitials(role.company)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[13.5px] font-semibold truncate"
                      style={{ color: "#17131F" }}
                    >
                      {role.role}
                    </div>
                    <div className="text-[12px]" style={{ color: "#7C7590" }}>
                      {role.company}
                    </div>
                  </div>
                  <span
                    className="text-[11px] font-mono shrink-0 mr-2"
                    style={{ color: "#8C86A0" }}
                  >
                    {formatDate(role.createdAt)}
                  </span>
                  {role.hasJd && (
                    <Link
                      to={`/invite/${role.token}/brief`}
                      className="text-[12px] font-semibold shrink-0"
                      style={{ color: "var(--accent)" }}
                    >
                      View JD →
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Practice History */}
        <Card>
          <CardHeader title="Practice History" />
          <div className="px-5 pb-2">
            {data.practiceHistory.length === 0 ? (
              <p className="text-[13px] py-2 italic" style={{ color: "#8C86A0" }}>
                No sessions yet. Complete a session to see your history here.
              </p>
            ) : (
              data.practiceHistory.slice(0, 5).map((entry, i) => (
                <div
                  key={entry.sessionId}
                  className="flex items-center gap-2.5 py-2"
                  style={{
                    borderBottom:
                      i < Math.min(data.practiceHistory.length, 5) - 1
                        ? "1px solid #F4F2F9"
                        : "none",
                  }}
                >
                  <TypePill type={entry.sessionType} />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[13px] font-semibold truncate"
                      style={{ color: "#17131F" }}
                    >
                      {entry.sessionType === "practise" ? "Full Session" : "Drill"}
                    </div>
                    <div className="text-[11px] font-mono" style={{ color: "#8C86A0" }}>
                      {formatDate(entry.createdAt)}
                    </div>
                  </div>
                  {entry.score != null && (
                    <span
                      className="text-[13px] font-bold font-mono shrink-0"
                      style={{
                        color: entry.sessionType === "practise" ? "#2FA872" : "#C05A2E",
                      }}
                    >
                      {entry.score}
                    </span>
                  )}
                  {entry.hasScorecard && (
                    <Link
                      to={`/invite/${token}/scorecard/${entry.sessionId}`}
                      className="text-[11px] font-semibold shrink-0"
                      style={{ color: "var(--accent)" }}
                    >
                      →
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Practice modes. Hidden entirely in review-only mode: every control in
          this card starts a session, which api_invite_start refuses with 410 on
          an expired invite, and its own copy ("start it directly") would be a
          lie. Past sessions stay reachable through Practice history above. */}
      {!reviewOnly && (
      <div className="mt-6">
        <Card>
          <CardHeader title="Practice modes" />
          <p className="px-5 pb-3 text-[12.5px]" style={{ color: "#7C7590" }}>
            Expand any mode to see what it covers, then start it directly.
          </p>
          <div style={{ borderTop: "1px solid #F4F2F9" }}>
            {data.modes.map((mode, i) => {
              const body = MODE_BODIES[mode.id];
              const locked = data.inviteMode === "full" && mode.id !== "full";
              return (
                <details
                  key={mode.id}
                  className="group"
                  style={{ borderTop: i > 0 ? "1px solid #F4F2F9" : undefined }}
                >
                  <summary
                    className="flex items-center justify-between px-5 py-3.5 cursor-pointer list-none"
                    style={{ userSelect: "none" }}
                  >
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-mono text-[9.5px] font-semibold uppercase mb-1"
                        style={{ color: "var(--accent)", letterSpacing: "0.06em" }}
                      >
                        {mode.tag}
                      </div>
                      <div className="text-[13.5px] font-semibold" style={{ color: "#17131F" }}>
                        {mode.name}
                      </div>
                      <div className="text-[12px]" style={{ color: "#7C7590" }}>
                        {mode.hint}
                      </div>
                    </div>
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="none"
                      className="shrink-0 mx-3 transition-transform group-open:rotate-180"
                      style={{ color: "#8C86A0" }}
                    >
                      <path
                        d="M4 6l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </summary>
                  <div className="px-5 pb-4">
                    {body && (
                      <p className="text-[13px] leading-relaxed mb-3" style={{ color: "#4A4260" }}>
                        {body}
                      </p>
                    )}
                    {locked ? (
                      <p className="text-[12px] italic" style={{ color: "#8C86A0" }}>
                        Mode locked for this assessment — use the Start Practise Session button above.
                      </p>
                    ) : (
                      <button
                        onClick={(e) => { e.preventDefault(); startSession(mode.id); }}
                        disabled={starting}
                        className="text-[12.5px] font-semibold px-4 py-1.5 rounded-full disabled:opacity-60"
                        style={{ background: "var(--accent)", color: "#fff" }}
                      >
                        {starting ? "Starting…" : `Start ${mode.name}`}
                      </button>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </Card>
      </div>
      )}
      </div>

      {/* Sticky Start Practice CTA — mobile only; sits in its own row directly above
          the HomeShell tab bar (var(--tab-bar-h), set on HomeShell's wrapper) rather
          than stacking on top of it, so the tab bar stays visible and reachable. */}
      {!reviewOnly && (
        <div
          className="fixed left-0 right-0 z-[60] md:hidden"
          style={{ background: "var(--accent)", bottom: "var(--tab-bar-h)" }}
        >
          <button
            onClick={() => startSession()}
            disabled={starting || !profileLoaded}
            className="w-full py-4 text-white text-[15px] font-semibold disabled:opacity-60"
          >
            {starting ? "Starting…" : "Start Practise Session"}
          </button>
        </div>
      )}
    </HomeShell>
  );
}
